import {
  AssetLink,
  AccountLink,
  ApplicationLink,
  ApplicationEscrowLink,
} from "@/components/links";
import { Separator } from "@/components/ui/separator";
import { cx } from "class-variance-authority";
import { ShieldCheck } from "lucide-react";
import { calculateBenchState, toLocaleString } from "@/lib/utils";
import { BenchStateFull } from "@myth-finance/bench-sdk";

export function AssetInfo({
  data,
  className,
  prequel,
  sequel,
}: {
  data: BenchStateFull;
  className?: string;
  prequel?: React.ReactNode;
  sequel?: React.ReactNode;
}) {
  const spacing = prequel || sequel ? "lg:gap-3" : "lg:gap-11";
  return (
    <div
      className={cx(
        `flex-col text-sm text-muted-foreground/90 flex min-w-[300px] w-full max-w-[350px] pt-2 pb-1 lg:py-0 px-6 gap-1 ${spacing} items-stretch`,
        className
      )}
    >
      {prequel}
      <div className="flex flex-col gap-1 group hover:text-foreground/80 transition-all">
        <div className="flex justify-between">
          <div className="text-muted-foreground/80 group-hover:text-foreground/80 transition-all text-lg">
            Selling
          </div>
          <ShieldCheck className="text-green-500/70 group-hover:text-green-500 transition-all" />
        </div>
        <div className="flex justify-between">
          <div className="transition-all text-muted-foreground/60 group-hover:text-foreground/80">
            Unit name
          </div>
          <div className="">{data.sellingUnitName}</div>
        </div>
        <div className="flex justify-between">
          <div className="transition-all text-muted-foreground/60 group-hover:text-foreground/80">
            Asset ID
          </div>
          <AssetLink id={data.sellingAssetId} />
        </div>
        <div className="flex justify-between">
          <div className="transition-all text-muted-foreground/60 group-hover:text-foreground/80">
            Pera verified
          </div>
          <div>Yes</div>
        </div>
      </div>
      <div className="flex flex-col gap-1 group hover:text-foreground/80 transition-all">
        <div className="flex justify-between mt-4 lg:mt-6">
          <div className="text-muted-foreground/80 group-hover:text-foreground/80 transition-all text-lg">
            Purchasing with
          </div>
          <ShieldCheck className="text-green-500/70 group-hover:text-green-500 transition-all" />
          {/* <ShieldX className="text-red-500" /> */}
        </div>
        <div className="flex justify-between">
          <div className="transition-all text-muted-foreground/60 group-hover:text-foreground/80">
            Unit name
          </div>
          <div className="">{data.purchaseUnitName}</div>
        </div>
        <div className="flex justify-between">
          <div className="transition-all text-muted-foreground/60 group-hover:text-foreground/80">
            Asset ID
          </div>
          <AssetLink id={data.purchaseAssetId} />
        </div>
        {/* <div className="flex justify-between text-destructive"> */}
        <div className="flex justify-between">
          <div className="transition-all text-muted-foreground/60 group-hover:text-foreground/80">
            Pera verified
          </div>
          <div>Yes</div>
        </div>
      </div>
      {sequel ? (
        <>
          <Separator className="mb-2 w-0" />
          {sequel}
        </>
      ) : null}{" "}
    </div>
  );
}

export function BenchInfo({
  data,
  className,
  prequel,
  sequel,
  tight,
}: {
  data: BenchStateFull;
  className?: string;
  prequel?: React.ReactNode;
  sequel?: React.ReactNode;
  tight?: boolean;
}) {
  if (!data.appId) return null;
  const benchState = calculateBenchState(data);
  return (
    <div
      className={cx(
        `flex-col text-sm text-muted-foreground/90 flex min-w-[300px] w-full max-w-[350px] pt-2 lg:pt-6 p-6 mt-0 gap-1 items-stretch`,
        className
      )}
    >
      {prequel}

      <Separator className="flex lg:hidden mt-3 mb-8 w-[66%] self-center" />

      <div className="flex flex-col gap-1 group hover:text-foreground/80 transition-all">
        <div className="flex justify-between">
          <div className="transition-all text-muted-foreground/60 group-hover:text-foreground/80">
            Seller
          </div>
          <AccountLink address={data.ownerAddr} />
        </div>
        <div className="flex justify-between">
          <div className="transition-all text-muted-foreground/60 group-hover:text-foreground/80">
            Bench Size
          </div>
          {toLocaleString(data.total)} {data.sellingUnitName}
        </div>
        {benchState === "open" ? (
          <div className="flex justify-between">
            <div className="transition-all text-muted-foreground/60 group-hover:text-foreground/80">
              Remaining
            </div>
            {toLocaleString(data.remainingTokens)} {data.sellingUnitName}
          </div>
        ) : (
          <div className="flex justify-between">
            <div className="transition-all text-muted-foreground/60 group-hover:text-foreground/80">
              {data.sellingUnitName} Price
            </div>
            {data.price} {data.purchaseUnitName}
          </div>
        )}
        {benchState === "upcoming" && data.purchaseLimit ? (
          <div className="flex justify-between">
            <div className="transition-all text-muted-foreground/60 group-hover:text-foreground/80">
              Account Limit{" "}
            </div>
            {toLocaleString(data.purchaseLimit / data.purchaseDecimalDenom)} {data.purchaseUnitName}
          </div>
        ) : (
          <div className="flex justify-between">
            <div className="transition-all text-muted-foreground/60 group-hover:text-foreground/80">
              Revenue{" "}
            </div>
            {toLocaleString(data.price * (data.total - data.remainingTokens))}{" "}
            {data.purchaseUnitName}
          </div>
        )}

        <div className="flex justify-between">
          <div className="transition-all text-muted-foreground/60 group-hover:text-foreground/80">
            Bench Fee
          </div>
          {data.feeBps / 100}%
        </div>
      </div>

      <Separator
        className={cx(
          "my-4 lg:my-8 w-[33%] self-center",
          tight ? "lg:my-2" : ""
        )}
      />

      <div className="flex flex-col gap-1 group hover:text-foreground/80 transition-all">
        <div className="flex justify-between">
          <div className="transition-all text-muted-foreground/60 group-hover:text-foreground/80">
            Bench ID
          </div>
          {data.benchId}
        </div>
        <div className="flex justify-between">
          <div className="transition-all text-muted-foreground/60 group-hover:text-foreground/80">
            Bench Escrow
          </div>
          <ApplicationEscrowLink id={data.appId} />
        </div>
        <div className="flex justify-between">
          <div className="transition-all text-muted-foreground/60 group-hover:text-foreground/80">
            Application ID
          </div>
          <ApplicationLink id={data.appId} />
        </div>
        <div className="flex justify-between">
          <div className="transition-all text-muted-foreground/60 group-hover:text-foreground/80">
            Start time
          </div>
          {data.timeStart
            ? new Date(data.timeStart * 1000).toLocaleString()
            : "-"}
        </div>
        <div className="flex justify-between">
          <div className="transition-all text-muted-foreground/60 group-hover:text-foreground/80">
            End time
          </div>
          {data.timeEnd ? new Date(data.timeEnd * 1000).toLocaleString() : "-"}
        </div>
      </div>

      {sequel ? (
        <>
          <Separator className="my-2" />
          {sequel}
        </>
      ) : null}
    </div>
  );
}
